import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, QueryList, signal, ViewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { interval } from 'rxjs';
import { AuthService } from '../auth.service';
import { getPasswordChecks, getPasswordStrength, passwordsMatchValidator, passwordStrengthValidator } from '../password.validators';

type Step = 1 | 2 | 3 | 4;


@Component({
  selector: 'app-reset-password',
  standalone: false,
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent {

  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private authService = inject(AuthService);

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  step: Step = 1;
  isSubmitting = false;
  resendLoading = false;
  errorMessage = signal('');

  showPassword = signal(false);
  showConfirmPassword = signal(false);
  submitted = signal(false)

  // Stored after step 2 succeeds — used as Bearer token in step 3
  private resetToken = signal('');
  // Stored after step 1 — needed for step 2 payload
  private userEmail = signal('');


  otpDuration = 10 * 60;
  countdown = this.otpDuration;

  emailForm = this.fb.group({
    email: this.fb.nonNullable.control('', [Validators.required, Validators.email]),
  });

  otpForm = this.fb.group({
    otp: this.fb.array(
      Array.from({ length: 6 }, () =>
        this.fb.nonNullable.control('', [
          Validators.required,
          Validators.pattern(/^[0-9]$/),
        ])
      )
    ),
  });

  passwordForm = this.fb.group(
    {
      password: this.fb.nonNullable.control('', [
        Validators.required,
        Validators.minLength(8),
        passwordStrengthValidator(),
      ]),
      confirmPassword: this.fb.nonNullable.control('', [Validators.required]),
    },
    { validators: [passwordsMatchValidator] }
  );


  constructor() {
    this.emailControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.step === 1) this.errorMessage.set('');
      });

    interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.step === 2 && this.countdown > 0) {
          this.countdown--;
        }
      });
  }

  get emailControl(): FormControl<string> {
    return this.emailForm.controls.email;
  }

  get otpArray(): FormArray<FormControl<string>> {
    return this.otpForm.controls.otp as FormArray<FormControl<string>>;
  }

  get passwordControl(): FormControl<string> {
    return this.passwordForm.controls.password;
  }

  get confirmPasswordControl(): FormControl<string> {
    return this.passwordForm.controls.confirmPassword;
  }

  get otpValue(): string {
    return this.otpArray.controls.map(control => control.value).join('');
  }

  get isOtpComplete(): boolean {
    return this.otpArray.controls.every(control => /^[0-9]$/.test(control.value));
  }

  get passwordChecks() {
    return getPasswordChecks(this.passwordControl.value);
  }

  get passwordStrength() {
    return getPasswordStrength(this.passwordControl.value);
  }


  get formattedCountdown(): string {
    const minutes = Math.floor(this.countdown / 60);
    const seconds = this.countdown % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  get canSubmitPassword(): boolean {
    return this.passwordForm.valid;
  }


  onSubmitEmail() {
    if (this.emailForm.invalid) {
      this.emailForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.errorMessage.set('');

    this.authService.sendCode(this.emailControl.value).subscribe({
      next: () => {
        this.userEmail.set(this.emailControl.value);
        this.isSubmitting = false;
        this.startOtpStep();
      },
      error: (err) => {
        this.isSubmitting = false;
        if (err.status === 404) {
          this.errorMessage.set('No account found with this email. Please verify your email address.');
          return;
        }
        if (err.status === 429) {
          this.errorMessage.set('Too many attempts. Please wait a minute and try again.');
          return;
        }
        this.errorMessage.set(
          err.error?.message ?? 'Something went wrong. Please try again.'
        );
      }
    })
  }

  togglePassword() {
    this.showPassword.update(v => !v);
  }

  toggleConfirmPassword() {
    this.showConfirmPassword.update(v => !v);
  }


  onVerifyOtp() {
    if (!this.isOtpComplete) {
      this.otpForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.errorMessage.set('');

    this.authService.verifyOtp(this.userEmail(), this.otpValue).subscribe({
      next: (res) => {
        this.resetToken.set(res.reset_token); // store reset JWT
        this.isSubmitting = false;
        this.step = 3;
      },
      error: (err) => {
        this.isSubmitting = false;
        this.errorMessage.set(
          err.error?.message ?? 'Invalid or expired code. Please try again.'
        );
        this.clearOtp();
        this.focusOtp(0);
      },
    });
  }

  onResetPassword() {
    this.submitted.set(true);

    if (this.passwordForm.invalid) {
      return;
    }

    this.isSubmitting = true;
    this.errorMessage.set('');

    this.authService
      .resetPassword(this.passwordControl.value, this.resetToken())
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.resetToken.set(''); // clear reset token — single use
          this.step = 4;
        },
        error: (err) => {
          this.isSubmitting = false;

          // 401 = reset token expired (15 min window passed)
          if (err.status === 401) {
            this.errorMessage.set(
              'Your reset session expired. Please start over.'
            );
            // Send user back to step 1 after a short delay
            setTimeout(() => this.restartFlow(), 2500);
            return;
          }

          this.errorMessage.set(
            err.error?.message ?? 'Failed to reset password. Please try again.'
          );
        },
      });

    // this.isSubmitting = true;

    // setTimeout(() => {
    //   this.isSubmitting = false;
    //   this.step = 4;
    //   // later: submit new password on backend
    //   // this.submitNewPassword(this.emailControl.value, this.passwordControl.value)
    // }, 800);
  }

  onOtpInput(index: number, event: Event) {
    const input = event.target as HTMLInputElement;
    const sanitized = input.value.replace(/\D/g, '').slice(-1);

    this.otpArray.at(index).setValue(sanitized);

    if (sanitized && index < this.otpArray.length - 1) {
      this.focusOtp(index + 1);
      this.errorMessage.set('');
    }
  }

  onOtpKeyDown(index: number, event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace' && !input.value && index > 0) {
      this.focusOtp(index - 1);
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusOtp(index - 1);
      return;
    }

    if (event.key === 'ArrowRight' && index < this.otpArray.length - 1) {
      event.preventDefault();
      this.focusOtp(index + 1);
    }
  }

  onOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') ?? '';
    const digits = pasted.replace(/\D/g, '').slice(0, 6).split('');

    digits.forEach((digit, index) => {
      this.otpArray.at(index).setValue(digit);
    });

    const targetIndex = Math.min(digits.length, 5);
    this.focusOtp(targetIndex);
  }

  resendCode() {
    if (this.countdown > 540) return;

    this.resendLoading = true;
    this.errorMessage.set('');

    this.authService.sendCode(this.userEmail()).subscribe({
      next: () => {
        this.resendLoading = false;
        this.countdown = this.otpDuration;
        this.clearOtp();
        this.focusOtp(0);
      },
      error: (err) => {
        this.resendLoading = false;
        if (err.status === 429) {
          this.errorMessage.set('Too many attempts. Please wait a minute and try again.');
          return;
        }
        this.errorMessage.set(
          err.error?.message ?? 'Failed to resend code. Please try again.'
        );
      },
    });
  }

  continueToSignIn() {
    // replace with router navigation
    this.router.navigate(['/login']);
  }


  private startOtpStep() {
    this.step = 2;
    this.countdown = this.otpDuration;
    this.clearOtp();

    queueMicrotask(() => this.focusOtp(0));
  }

  private clearOtp() {
    this.otpArray.controls.forEach(control => control.setValue(''));
  }

  private focusOtp(index: number) {
    const input = this.otpInputs?.get(index)?.nativeElement;
    input?.focus();
    input?.select();
  }

  private restartFlow(): void {
    this.step = 1;
    this.errorMessage.set('');
    this.emailForm.reset();
    this.clearOtp();
    this.passwordForm.reset();
    this.submitted.set(false);
  }

}
